import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { runNeighborhoodClusters } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { getResultNameTypesByOrchestrations, ResultNameOrchestrations } from 'interfaces/calculateFeatures';
import { CellTypingConfig } from 'interfaces/inferenceArtifacts';
import { filter, find, flatMap, forEach, get, includes, indexOf, isEmpty, join, map, size, some, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import * as yup from 'yup';
import { ActionStepper } from '../ActionStepper';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { JobNameAndDescriptionStep } from '../utils';
import { ClusteringParamsList } from './ClusteringParametersStep';
import { defaultValues, maxMaxK, minMinK } from './consts';
import { SamplingParametersStep } from './SamplingParametersStep';
import {
  NeighborhoodClustersFormValues,
  runNeighborhoodClustersOrder,
  RunNeighborhoodClustersStep,
  SamplingParams,
} from './types';

const SNACK_BAR_KEY_RUN_NEIGHBORHOOD_CLUSTERS = 'RUN_NEIGHBORHOOD_CLUSTERS';

export const RunNeighborhoodClusters = (props: RunNeighborhoodClustersProps): ReactElement => {
  const { onClose, casesParams } = props;
  const studyId = casesParams.filters?.studyId;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const orchestrationSelectionState = useOrchestrationSelections();
  const { selectedPipsOrchestrations } = orchestrationSelectionState;

  const [activeStepIndex, setActiveStep] = useState(0);
  const activeStep = runNeighborhoodClustersOrder[activeStepIndex];

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const currentValidationSchema = validationSchema[activeStep];
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<NeighborhoodClustersFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: defaultValues,
  });
  useTrackActionFormDirty(isDirty);

  const validateSlidesResultsCount = () => {
    const slideIds = map(slides, 'slideId');
    const doesSlideIdHaveCellsTableArtifact = (slideId: string) => {
      return some(watch('configParams.cellTableArtifactUrls'), (cellsTableArtifactUrl) =>
        includes(cellsTableArtifactUrl, slideId)
      );
    };

    const slideIdsWithoutResult = filter(slideIds, (slideId) => !doesSlideIdHaveCellsTableArtifact(slideId));

    if (size(slideIdsWithoutResult) > 0) {
      setError(
        'configParams.cellTableArtifactUrls',
        {
          type: 'manual',
          message: `The Following Slides do not have Cell Typing Results (Cell Tables): ${join(
            slideIdsWithoutResult,
            ', '
          )}`,
        },
        { shouldFocus: true }
      );
      return false;
    }
    return true;
  };

  const checkValidationAndSetIsStepFailed = async (step: RunNeighborhoodClustersStep) => {
    const stepIndex = indexOf(runNeighborhoodClustersOrder, step);
    let isValid = await trigger();

    if (activeStep === RunNeighborhoodClustersStep.SetUpCellTableInputs) {
      validateSlidesResultsCount();
      if (!isValid) {
        isValid = false;
      }
    }

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const {
    data: pipsCellTypingArtifacts,
    isLoading: isPipsCellTypingLoading,
    isFetching: isPipsCellTypingFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: CellTypingConfig,
  });

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal),
    enabled: Boolean(studyId),
  });

  useEffect(() => {
    const finalInferenceResultsArtifactUrls: string[] = [];

    const slidesWithResults = filter(slides, (slide) => {
      return !isEmpty(selectedPipsOrchestrations[slide.slideId]);
    });

    forEach(slidesWithResults, (slide) => {
      forEach(selectedPipsOrchestrations[slide.slideId], (orchestrationData) => {
        finalInferenceResultsArtifactUrls.push(
          orchestrationData?.orchestration.orchestrationResultArtifactUrlPattern.replace('{slide_id}', slide.slideId)
        );
      });
    });

    setValue('configParams.cellTableArtifactUrls', finalInferenceResultsArtifactUrls);
    trigger('configParams.cellTableArtifactUrls');
  }, [selectedPipsOrchestrations, setValue, trigger, slides]);

  const cellClasses = useMemo(() => {
    return uniq(
      flatMap(selectedPipsOrchestrations, (orchestrations) =>
        flatMap(
          orchestrations,
          (orchestrationData) =>
            (find(orchestrationData?.orchestration?.params?.data, { label: 'label' })?.options as string[]) || []
        )
      )
    );
  }, [selectedPipsOrchestrations]);

  const runNeighborhoodClustersMutation = useMutationWithErrorSnackbar({
    mutationFn: runNeighborhoodClusters,
    mutationDescription: 'run neighborhood clusters',
    onSuccess: () => {
      enqueueSnackbar('Neighborhood Clusters Started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_NEIGHBORHOOD_CLUSTERS);
    },
  });

  const onSubmit: SubmitHandler<any> = (data) => {
    runNeighborhoodClustersMutation.mutate({
      ...casesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Neighborhood Clusters to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_NEIGHBORHOOD_CLUSTERS,
      autoHideDuration: null,
    });
  };

  const stepsMap = {
    [RunNeighborhoodClustersStep.NameAndDescription]: {
      label: 'Job Name and Description',
      subLabel: activeStep !== RunNeighborhoodClustersStep.NameAndDescription && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    [RunNeighborhoodClustersStep.SetUpCellTableInputs]: {
      label: 'Setup Cell Table inputs',
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {get(errors, 'configParams.cellTableArtifactUrls') && (
              <Typography color="error">{get(errors, 'configParams.cellTableArtifactUrls')?.message}</Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={getResultNameTypesByOrchestrations(pipsCellTypingArtifacts)}
              pipsResultsArtifacts={pipsCellTypingArtifacts}
              displayInferenceModels={false}
              displayPipsResults
              {...orchestrationSelectionState}
              isLoading={isPipsCellTypingLoading || isPipsCellTypingFetching || isSlidesFetching}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunNeighborhoodClustersStep.SetUpCellTableInputs),
    },
    [RunNeighborhoodClustersStep.ClusteringParameters]: {
      label: 'Neighborhood Clustering Parameters',
      subLabel:
        'Assign a neighborhood ID to each cell by clustering its local microenvironment. For each cell, the composition of nearby neighbor cell types is computed and clustered to identify recurring neighborhood patterns',
      content: (
        <ClusteringParamsList
          clusteringParamsList={watch('configParams.clusteringParams')}
          setClusteringParamsList={(params) => {
            setValue('configParams.clusteringParams', params);
            trigger('configParams.clusteringParams');
          }}
          errors={errors}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunNeighborhoodClustersStep.ClusteringParameters),
    },
    [RunNeighborhoodClustersStep.SamplingParameters]: {
      label: 'Sampling Parameters',
      subLabel:
        'Subsample cells per slide before computing neighbor counts and clustering. Sampling reduces runtime and memory while keeping representative cell-class composition (or enforcing balanced sampling) according to the options below',
      content: (
        <SamplingParametersStep
          samplingParams={watch('configParams.samplingParams')}
          setSamplingParams={(params: SamplingParams) => {
            setValue('configParams.samplingParams', params);
            trigger('configParams.samplingParams');
          }}
          errors={get(errors, 'configParams.samplingParams')}
          cellClasses={cellClasses}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunNeighborhoodClustersStep.SamplingParameters),
    },
  };

  const steps = map(runNeighborhoodClustersOrder, (stepId) => stepsMap[stepId]);

  return (
    <ActionStepper
      handleSubmit={handleSubmit(onSubmit)}
      steps={steps}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={isStepFailed}
      validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
    />
  );
};

const validationSchema = {
  [RunNeighborhoodClustersStep.NameAndDescription]: yup.object({}),
  [RunNeighborhoodClustersStep.SetUpCellTableInputs]: yup.object().shape({
    configParams: yup.object().shape({
      cellTableArtifactUrls: yup
        .array()
        .required('Cells table is required')
        .min(1, 'Please select Cell Typing Results (Cell Tables)'),
    }),
  }),
  [RunNeighborhoodClustersStep.ClusteringParameters]: yup.object({
    configParams: yup.object().shape({
      clusteringParams: yup.array().of(
        yup.object().shape({
          kMeansParams: yup.object().shape({
            minK: yup
              .number()
              .min(minMinK, `Min K must be greater than or equal to ${minMinK}`)
              .lessThan(maxMaxK, `Min K must be less than ${maxMaxK}`),
            maxK: yup
              .number()
              .min(yup.ref('minK'), 'Max K must be greater or equal to Min K')
              .max(maxMaxK, `Max K must be less than or equal to ${maxMaxK}`),
          }),
          neighborCountParams: yup.object().shape({
            nNeighbors: yup.number().required('N Neighbors is required'),
            normalizeCounts: yup.boolean(),
            arcsinhNormalizationCoFactor: yup
              .number()
              .min(1, 'arcsinh Normalization Coefficient must be at least 1')
              .required('Arcsinh Normalization Coefficient is required'),
          }),
        })
      ),
    }),
  }),
  [RunNeighborhoodClustersStep.SamplingParameters]: yup.object({
    configParams: yup.object().shape({
      samplingParams: yup.object().shape({
        absoluteMaxCellsPerSlide: yup
          .number()
          .required('Absolute max cells per slide is required')
          .min(1, 'Must be at least 1'),
        maxCellsFractionPerSlide: yup
          .number()
          .required('Max cells fraction per slide is required')
          .moreThan(0, 'Must be greater than 0')
          .max(1, 'Cannot exceed 1'),
        cellClassesToExclude: yup.array().of(yup.string()).nullable().default(null),
        weightedSamplingOfCellClasses: yup.boolean().required('Weighted sampling of cell classes is required'),
      }),
    }),
  }),
};

export interface RunNeighborhoodClustersProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
}
